/**
 * Roster identity helpers — turn a coach-supplied `TacticsRosterEntry`
 * into the labels rendered on player tokens and brief cards.
 *
 * Convention:
 *   - Token label: jersey number if set; else initials from the name;
 *     else `idx + 1` (the legacy fall-through).
 *   - Brief card title: `#11 MS — pressing-forward` when both name +
 *     number exist; degrades gracefully otherwise.
 *
 * Initials cap at 2 chars so a long name doesn't overflow a small
 * token roundel. Hyphenated last names use the FIRST letter of the
 * combined surname ("Trent Alexander-Arnold" → "TA").
 */

import type { TacticsRosterEntry } from '@4cm/core/tactics';

export interface RosterIdentity {
  /** Short label for the token (initials or number). */
  token: string;
  /** Long-form label for cards / lists ("#11 MS"). */
  card: string;
  /** True when at least one of name / number is set. */
  filled: boolean;
}

/** Derive 1-2 letter initials from a free-text name. Returns empty
 *  string on missing / whitespace input so callers can branch. */
export function initialsFromName(name: string | undefined): string {
  if (!name) return '';
  // Split on whitespace, dropping empties; take first letter of each
  // word; cap at 2. Hyphens are NOT split — "Alexander-Arnold" is
  // treated as one surname (matches scoreboard convention).
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 0);
  if (words.length === 0) return '';
  if (words.length === 1) {
    // Single name (e.g. "Pelé") — take the first two letters.
    return words[0]!.slice(0, 2).toUpperCase();
  }
  const first = words[0]!.charAt(0);
  const last = words[words.length - 1]!.charAt(0);
  return (first + last).toUpperCase();
}

/**
 * Compose the token + card labels for one player slot.
 *
 *   index    — 0-based playerKey index (`home-3` → 3). Used as the
 *              fallback when nothing is set in the roster.
 *   entry    — coach's roster entry for this key (may be undefined).
 */
export function rosterIdentityFor(
  index: number,
  entry: TacticsRosterEntry | undefined,
): RosterIdentity {
  const number = entry?.number;
  const name = entry?.name?.trim();
  const initials = initialsFromName(name);
  const filled = !!(name || typeof number === 'number');

  // Token label — prefer jersey number (universally readable on a
  // tiny roundel), then initials, then the slot index.
  const token =
    typeof number === 'number'
      ? String(number)
      : initials || String(index + 1);

  // Card label — combine when we have both, otherwise the richer
  // available piece.
  let card: string;
  if (typeof number === 'number' && initials) {
    card = `#${number} ${initials}`;
  } else if (typeof number === 'number') {
    card = `#${number}`;
  } else if (initials) {
    card = initials;
  } else {
    card = `#${index + 1}`;
  }

  return { token, card, filled };
}
